import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listTeachers, deleteTeacher } from "../services/teacher";
import { getUserInfo } from "../services/auth";
import { useConfirm } from "../hook/useConfirm";

const PAGE_SIZE = 8;

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
};

const subjectsOf = (teacher) => teacher?.subject || teacher?.modules || [];

const ListaProfessoresLayer = () => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirm();
  const navigate = useNavigate();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [removingId, setRemovingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchTeachers = () => {
    setLoading(true);
    listTeachers()
      .then((resp) => setTeachers(resp.data || []))
      .catch(() => setTeachers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return teachers;
    return teachers.filter(
      (t2) =>
        t2.name?.toLowerCase().includes(term) ||
        t2.email?.toLowerCase().includes(term) ||
        t2.contact?.toLowerCase().includes(term),
    );
  }, [teachers, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (next) => {
    if (next < 1 || next > totalPages) return;
    setPage(next);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch("");
    setPage(1);
  };

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const handleEdit = (teacher) => {
    navigate(`/professores/editar/${teacher.id}`, { state: { teacher } });
  };

  const handleRemove = async (teacher) => {
    if (!(await confirm(t("professor.list.remove.confirm")))) return;
    setRemovingId(teacher.id);
    try {
      await deleteTeacher(teacher.id, getUserInfo()?.id);
      toast.success(t("professor.list.remove.success"));
      fetchTeachers();
    } catch (err) {
      toast.error(t("professor.list.remove.error"));
    } finally {
      setRemovingId(null);
    }
  };

  const withSubjectsCount = teachers.filter((t2) => subjectsOf(t2).length > 0).length;

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='d-flex flex-wrap align-items-center gap-3'>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='solar:users-group-rounded-bold' />
              </span>
              <div>
                <h6 className='mb-0'>{teachers.length}</h6>
                <span className='text-secondary-light text-sm'>
                  {t("professor.list.stat.total")}
                </span>
              </div>
            </div>
          </div>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-info-focus text-info-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='solar:square-academic-cap-outline' />
              </span>
              <div>
                <h6 className='mb-0'>{withSubjectsCount}</h6>
                <span className='text-secondary-light text-sm'>
                  {t("professor.list.stat.withSubjects")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t("professor.list.title")}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("professor.list.subtitle")}</p>
              </div>
              <button
                type='button'
                className='btn btn-primary-600 d-flex align-items-center gap-2'
                onClick={() => navigate("/professores/cadastrar")}
              >
                <Icon icon='ic:baseline-plus' />
                {t("professor.list.add")}
              </button>
            </div>

            <div className='p-16 radius-8 bg-neutral-50 mb-24'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-8 col-lg-6'>
                  <label className='form-label text-sm mb-8'>{t("global.name")}</label>
                  <div className='icon-field'>
                    <span className='icon top-50 translate-middle-y'>
                      <Icon icon='ion:search-outline' />
                    </span>
                    <input
                      type='text'
                      className='form-control'
                      placeholder={t("professor.list.search.placeholder")}
                      value={search}
                      onChange={handleSearch}
                    />
                  </div>
                </div>
                <div className='col-lg-1'>
                  <button
                    type='button'
                    className='btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center'
                    title={t("global.clean.fields")}
                    onClick={clearFilters}
                  >
                    <Icon icon='ic:round-refresh' />
                  </button>
                </div>
              </div>
            </div>

            <div className='table-responsive scroll-sm'>
              <table className='table bordered-table mb-0'>
                <thead>
                  <tr>
                    <th scope='col' style={{ width: 36 }} />
                    <th scope='col'>{t("global.name")}</th>
                    <th scope='col'>{t("global.email")}</th>
                    <th scope='col'>{t("global.contact")}</th>
                    <th scope='col'>{t("global.register.date")}</th>
                    <th scope='col' className='text-end'>
                      {t("global.col.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={6} className='text-center text-secondary-light py-32'>
                        {t("professor.list.loading")}
                      </td>
                    </tr>
                  )}
                  {!loading && pageItems.length === 0 && (
                    <tr>
                      <td colSpan={6} className='text-center text-secondary-light py-32'>
                        {t("professor.list.empty")}
                      </td>
                    </tr>
                  )}
                  {pageItems.map((teacher) => {
                    const isExpanded = expandedId === teacher.id;
                    const busy = removingId === teacher.id;
                    const subjects = subjectsOf(teacher);
                    return (
                      <React.Fragment key={teacher.id}>
                        <tr
                          role='button'
                          onClick={() => toggleExpand(teacher.id)}
                          className={isExpanded ? "bg-neutral-50" : ""}
                        >
                          <td>
                            <Icon
                              icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                              className='text-secondary-light text-xl'
                            />
                          </td>
                          <td className='fw-semibold text-primary-light'>{teacher.name}</td>
                          <td>{teacher.email || "-"}</td>
                          <td>{teacher.contact || "-"}</td>
                          <td>{formatDate(teacher.createdAt)}</td>
                          <td className='text-end' onClick={(e) => e.stopPropagation()}>
                            <div className='d-flex align-items-center justify-content-end gap-3'>
                              <button
                                type='button'
                                className='btn btn-outline-primary-600 btn-sm d-flex align-items-center gap-1'
                                onClick={() => handleEdit(teacher)}
                              >
                                <Icon icon='solar:pen-2-linear' /> {t("global.edit")}
                              </button>
                              <button
                                type='button'
                                className='btn btn-outline-danger-600 btn-sm d-flex align-items-center gap-1'
                                disabled={busy}
                                onClick={() => handleRemove(teacher)}
                              >
                                <Icon icon='mingcute:delete-2-line' /> {t("professor.list.remove")}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={6} className='p-0'>
                              <div className='p-24 bg-neutral-25 border-top'>
                                <div className='row gy-3'>
                                  <div className='col-md-3 text-center'>
                                    <div className='w-160-px h-160-px radius-12 border overflow-hidden bg-neutral-100 d-flex align-items-center justify-content-center mx-auto shadow-sm'>
                                      <Icon
                                        icon='solar:user-rounded-bold'
                                        className='text-3xl text-neutral-400'
                                      />
                                    </div>
                                    <div className='mt-16 d-flex flex-column align-items-center gap-8'>
                                      <span className='px-12 py-4 radius-8 fw-medium text-sm bg-success-focus text-success-main'>
                                        {t("global.active").toUpperCase()}
                                      </span>
                                      {subjects.length > 0 && (
                                        <span className='px-12 py-4 radius-8 fw-medium text-sm bg-primary-50 text-primary-600 d-flex align-items-center gap-1'>
                                          <Icon icon='solar:square-academic-cap-outline' />
                                          {subjects.length}{" "}
                                          {t("professor.field.subjects")}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className='col-md-9'>
                                    <ul
                                      className='nav nav-pills mb-16 gap-2'
                                      id={`professor-tabs-${teacher.id}`}
                                      role='tablist'
                                    >
                                      <li className='nav-item' role='presentation'>
                                        <button
                                          className='nav-link active radius-8 text-sm'
                                          data-bs-toggle='pill'
                                          data-bs-target={`#personal-prof-${teacher.id}`}
                                          type='button'
                                          role='tab'
                                        >
                                          {t("professor.expand.tab.personal")}
                                        </button>
                                      </li>
                                      <li className='nav-item' role='presentation'>
                                        <button
                                          className='nav-link radius-8 text-sm'
                                          data-bs-toggle='pill'
                                          data-bs-target={`#academic-prof-${teacher.id}`}
                                          type='button'
                                          role='tab'
                                        >
                                          {t("professor.expand.tab.academic")}
                                        </button>
                                      </li>
                                    </ul>
                                    <div className='tab-content'>
                                      <div
                                        className='tab-pane fade show active'
                                        id={`personal-prof-${teacher.id}`}
                                        role='tabpanel'
                                      >
                                        <table className='table table-bordered table-sm mb-0 bg-base'>
                                          <tbody>
                                            <tr>
                                              <th className='w-25 bg-neutral-50'>
                                                {t("professor.field.name")}
                                              </th>
                                              <td colSpan={3}>
                                                <strong>{teacher.name}</strong>
                                              </td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>{t("global.email")}</th>
                                              <td>{teacher.email || "-"}</td>
                                              <th className='bg-neutral-50'>{t("global.contact")}</th>
                                              <td>{teacher.contact || "-"}</td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("global.birth.date")}
                                              </th>
                                              <td>{formatDate(teacher.birthDate)}</td>
                                              <th className='bg-neutral-50'>
                                                {t("global.marital.status")}
                                              </th>
                                              <td>{teacher.maritalStatus || "-"}</td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                      <div
                                        className='tab-pane fade'
                                        id={`academic-prof-${teacher.id}`}
                                        role='tabpanel'
                                      >
                                        <table className='table table-bordered table-sm mb-0 bg-base'>
                                          <tbody>
                                            <tr>
                                              <th className='w-25 bg-neutral-50'>
                                                {t("global.educational.level")}
                                              </th>
                                              <td colSpan={3}>{teacher.educationLevel || "-"}</td>
                                            </tr>
                                            <tr>
                                              <th className='bg-neutral-50'>
                                                {t("professor.field.subjects")}
                                              </th>
                                              <td colSpan={3}>
                                                {subjects.length > 0
                                                  ? subjects.map((s) => s.name || s.subjectName).join(", ")
                                                  : "-"}
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mt-16'>
              <span className='text-secondary-light text-sm'>
                {filtered.length} {t("professor.list.total")}
              </span>
              {!loading && filtered.length > PAGE_SIZE && (
                <ul className='pagination d-flex flex-wrap align-items-center gap-2 mb-0'>
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                    >
                      <Icon icon='ep:d-arrow-left' className='text-xl' />
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <li key={p} className='page-item'>
                      <button
                        type='button'
                        className={`page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px ${
                          p === page ? "bg-primary-600 text-white" : "bg-primary-50 text-secondary-light"
                        }`}
                        onClick={() => goToPage(p)}
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      <Icon icon='ep:d-arrow-right' className='text-xl' />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      {ConfirmModal}
    </div>
  );
};

export default ListaProfessoresLayer;
